import React from 'react';
import cx from 'classnames';
import chrevronRight from '@ingka/ssr-icon/paths/chevron-right-small';
import magnifyingGlass from '@ingka/ssr-icon/paths/magnifying-glass';
import SSRIcon from '@ingka/ssr-icon';

import CatalogStatus from '../../CatalogStatus';
import CatalogIcon from '../../Icons/CatalogIcon/CatalogIcon';
import ItemNoPlate from '../../ItemNoPlate/ItemNoPlate';
import ReasonCodes from '../../Tree/NodeContent/SmallBanner/banners/ReasonCodes';
import { formatItemNo } from '../../../utils/data/utils';
import styles from './SearchPanel.module.scss';
import type { Item } from '../../../entities/Item';
import type { Catalog } from '../../../entities/Catalog';
import SearchFlag from './SearchFlag';

interface SearchSuggestion {
  id: string;
  title: string;
  type: string;
  data: unknown;
}

interface SearchSuggestionsProps {
  isSearching: boolean;
  emptyResult: boolean | null;
  structureReasonText?: string | null;
  searchConditionString?: string;
  item: Item | null;
  catalogList: Catalog[];
  suggestions: SearchSuggestion[];
  availableFlags: Record<string, number>;
  selectedSuggestion: string | null | undefined;
  active: boolean;
  onSelectSuggestion: (id: string, title: string) => void;
  onSelectFlag: (flag: string) => void;
  setSelectedIndex: (index: number) => void;
}

const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({
  isSearching,
  emptyResult,
  structureReasonText,
  searchConditionString,
  item,
  catalogList,
  suggestions,
  availableFlags,
  selectedSuggestion,
  active,
  onSelectSuggestion,
  onSelectFlag,
  setSelectedIndex,
}) => {
  return (
    <div className={styles.suggestions}>
      {isSearching && <span className={styles.searching}>Searching...</span>}
      {!isSearching && emptyResult ? (
        <p className="u-margin-large--bottom">
          {structureReasonText ? (
            <>
              <ItemNoPlate itemNoFormated={formatItemNo(item?.itemNo)} />
              <strong>Not valid for structure</strong>
              {` - ${structureReasonText}`}
              <br />
              <ReasonCodes />
            </>
          ) : (
            `No hits for "${searchConditionString}"`
          )}{' '}
        </p>
      ) : (
        catalogList.length > 0 && (
          <div className="u-margin-large--bottom">
            <p>
              <ItemNoPlate itemNoFormated={formatItemNo(item?.itemNo)} /> was found
              in:
            </p>
            <ul className={styles.catalogs}>
              {catalogList.map((catalog) => (
                <li key={catalog.catalogId}>
                  <CatalogIcon catalogType={catalog.catalogType} />{' '}
                  {catalog.catalogName}
                  <CatalogStatus catalog={catalog} />
                </li>
              ))}
            </ul>
            <p className={styles.hint}>
              We cant send you there, but you can try switching catalogue and do a
              new search
            </p>
          </div>
        )
      )}
      {!!suggestions.length && (
        <ul className={styles.suggestionGroup} role="listbox" id="suggestions-list">
          {suggestions.map((suggestion, key) => (
            <li
              key={`${suggestion.type}:${suggestion.id}`}
              role="option"
              aria-selected={selectedSuggestion === suggestion.id}
            >
              <button
                aria-label={suggestion.title}
                className={cx(styles.suggestion, {
                  [styles.selected]: selectedSuggestion === suggestion.id,
                })}
                onMouseEnter={() => setSelectedIndex(key)}
                onMouseLeave={() => setSelectedIndex(-1)}
                onMouseDown={(e) => {
                  // Prevent blur on input
                  e.preventDefault();
                  onSelectSuggestion(suggestion.id, suggestion.title);
                }}
                type="button"
              >
                <SSRIcon paths={magnifyingGlass} />
                <span className={styles.title}>{suggestion.title}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {Object.keys(availableFlags).length > 0 && (
        <>
          <div>Find a tag</div>
          <ul
            aria-label="tags"
            className={cx(styles.suggestionGroup, {
              [styles.active]: active,
            })}
            role="listbox"
          >
            {Object.entries(availableFlags).map(([flag, count], i) => (
              <li
                key={flag}
                role="option"
                aria-selected={selectedSuggestion === flag}
              >
                <button
                  aria-label={flag}
                  className={cx(styles.suggestion, {
                    [styles.selected]: selectedSuggestion === flag,
                  })}
                  onMouseDown={(e) => {
                    // Prevent blur on input
                    e.preventDefault();
                    onSelectFlag(flag);
                  }}
                  onMouseEnter={() => setSelectedIndex(suggestions.length + i)}
                  onMouseLeave={() => setSelectedIndex(-1)}
                  type="button"
                >
                  <SSRIcon paths={chrevronRight} />
                  <SearchFlag flag={flag} />
                  <span className={styles.count}>{count}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default SearchSuggestions;
